import React, { useEffect, useRef, useState } from "react";
import {
  GETQUICKITEMS,
  SUCCESS
} from "../../constants/constant";
import utilities from "../../utilities/utilities";
import { snpAxios } from "../global/api";
import "./GlobalRestoProductSearch.css";

const GlobalRestoProductSearch = ({ open, onClose, onSelect, baseUrl, shopHash }) => {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setItems([]);
    setActiveIndex(0);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 3 || !shopHash) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    const searchTimer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const encodedHash = utilities.encode(shopHash);
        const response = await snpAxios.get(
          `${baseUrl}${GETQUICKITEMS}?s=${encodedHash}&q=${encodeURIComponent(query.trim())}&m=1`,
        );
        if (!active) return;
        setItems(response.data.status === SUCCESS ? response.data.jcJson.value_2 || [] : []);
        setActiveIndex(0);
      } catch (error) {
        if (active) {
          console.error("Global restaurant item search failed:", error);
          setItems([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(searchTimer);
    };
  }, [baseUrl, open, query, shopHash]);

  useEffect(() => {
    resultsRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  const selectItem = (item) => {
    if (!item) return;
    onClose();
    onSelect(item);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "ArrowDown" && items.length) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % items.length);
    } else if (event.key === "ArrowUp" && items.length) {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + items.length) % items.length);
    } else if (event.key === "Enter" && items.length) {
      event.preventDefault();
      selectItem(items[activeIndex]);
    }
  };

  return (
    <div className="global-resto-search" role="presentation" onMouseDown={onClose}>
      <section
        aria-label="Search restaurant products"
        aria-modal="true"
        className="global-resto-search__panel"
        onKeyDown={handleSearchKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="global-resto-search__heading">
          <div>
            <span>Restaurant inventory</span>
            <h2>Find and edit an item</h2>
          </div>
          <kbd>Esc</kbd>
        </div>
        <div className="global-resto-search__input-wrap">
          <span aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            aria-label="Search dish name"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Type at least 3 letters…"
            value={query}
          />
          {loading ? <small>Searching…</small> : null}
        </div>
        <div ref={resultsRef} className="global-resto-search__results" role="listbox">
          {query.trim().length < 3 ? (
            <div className="global-resto-search__hint">Search by any item or display name.</div>
          ) : !loading && !items.length ? (
            <div className="global-resto-search__hint">No restaurant items found.</div>
          ) : items.map((item, index) => (
            <button
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "is-active" : ""}
              key={`${item.id}-${index}`}
              onClick={() => selectItem(item)}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
              type="button"
            >
              <span className="global-resto-search__item-copy">
                <strong>{item.name}</strong>
                {item.price?.name ? <small>{item.price.name}</small> : null}
              </span>
              <span className="global-resto-search__rate">₹{item.price?.sAmt ?? "—"}</span>
            </button>
          ))}
        </div>
        <footer>
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>Enter</kbd> edit item</span>
        </footer>
      </section>
    </div>
  );
};

export default GlobalRestoProductSearch;
